// frontend/src/components/concierge/tabs/VideoTab.jsx
// Video tab: the athlete's last 5 Hudl highlight reels, read live from their
// public Hudl profile (spec docs/superpowers/specs/2026-10-09-concierge-hudl-video-design.md).
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { fmtMonDay } from '../../../lib/conciergeActivityModel';
import { fmtDuration, hudlAgoText, hudlUpdateTone, shortMp4 } from '../../../lib/hudlModel';

const MESSAGES = {
  no_link: () => 'No Hudl link on this profile.',
  invalid_link: (e) => `This Hudl link isn't a profile we can read: ${e}`,
  profile_not_found: (e) => `Hudl says this profile doesn't exist: ${e}`,
  private: () => 'This Hudl profile is private.',
  hudl_unavailable: () => "Couldn't reach Hudl right now. Try again later.",
};
const EDITABLE = new Set(['no_link', 'invalid_link', 'profile_not_found']);

function Mp4({ url, quality }) {
  const [copied, setCopied] = useState(false);
  const copy = () => navigator.clipboard.writeText(url).then(() => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  });
  return (
    <span className="cn-video__mp4">
      .mp4 ({quality}p): <a href={url} target="_blank" rel="noopener noreferrer">{shortMp4(url)}</a>
      <button type="button" className="cn-fpill" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
    </span>
  );
}

export default function VideoTab({ userId, onEditProfile }) {
  const { API_URL } = useAuth();
  const [data, setData] = useState(null);   // null = loading

  const load = useCallback(() => {
    setData(null);
    axios.get(`${API_URL}/concierge/${userId}/hudl`,
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(res => setData(res.data))
      .catch(() => setData({ status: 'hudl_unavailable' }));
  }, [API_URL, userId]);
  useEffect(() => { load(); }, [load]);

  if (data === null) {
    // David sees ~5 s on staging (2026-10-09); a cold server-side probe took 10–14 s.
    return (
      <div className="cn-tabbody">
        <section className="cn-card">
          <div className="cn-video__checking" role="status" aria-live="polite">
            <span className="cn-video__spinner" aria-hidden="true" />
            <div>
              <div className="cn-video__checking-title">Checking Hudl…</div>
              <div className="cn-video__checking-sub">Pulling their latest reels from Hudl. This usually takes about 5–10 seconds.</div>
            </div>
          </div>
        </section>
      </div>
    );
  }
  if (data.status !== 'ok') {
    const msg = (MESSAGES[data.status] || MESSAGES.hudl_unavailable)(data.entered || '');
    return (
      <div className="cn-tabbody">
        <section className="cn-card">
          <div className="cn-card__head"><h2 className="cn-card__title">Video</h2></div>
          <div className="cn-empty-line">{msg}</div>
          <div className="cn-video__actions">
            {EDITABLE.has(data.status) && <button type="button" className="cn-card__action" onClick={onEditProfile}>Edit profile →</button>}
            {data.status === 'hudl_unavailable' && <button type="button" className="cn-card__action" onClick={load}>Retry</button>}
          </div>
        </section>
      </div>
    );
  }

  const now = new Date();
  return (
    <div className="cn-tabbody">
      <section className="cn-card">
        <div className="cn-card__head">
          <h2 className="cn-card__title">Video</h2>
          <span className="cn-card__sub">Hudl · {data.reel_count} reels · {Number(data.total_views || 0).toLocaleString('en-US')} total views</span>
          <a className="cn-card__action" href={data.profile_url} target="_blank" rel="noopener noreferrer">View Hudl profile →</a>
        </div>
        <div className="cn-video__dates">
          <span>Last Hudl update{' '}
            <strong data-testid="hudl-last-update" className={`cn-tone--${hudlUpdateTone(data.last_update_at, now)}`}>
              {data.last_update_at ? `${hudlAgoText(data.last_update_at, now)} · ${fmtMonDay(data.last_update_at)}` : 'Never'}
            </strong></span>
          <span>Last seen on Hudl{' '}
            <strong>{data.last_seen_at ? `${hudlAgoText(data.last_seen_at, now)} · ${fmtMonDay(data.last_seen_at)}` : 'Not available'}</strong></span>
        </div>
        {data.reels.length === 0 ? <div className="cn-empty-line">No highlight reels yet.</div> : data.reels.map(r => (
          <div key={r.hudl_url || r.title} className="cn-row cn-video__row">
            {r.thumbnail_url ? <img className="cn-video__thumb" src={r.thumbnail_url} alt="" /> : <span className="cn-video__thumb" />}
            <div className="cn-video__main">
              <div className="cn-video__title">{r.title}</div>
              <div className="cn-video__links">
                {r.hudl_url && <a href={r.hudl_url} target="_blank" rel="noopener noreferrer">Watch on Hudl →</a>}
                {r.mp4_url && <Mp4 url={r.mp4_url} quality={r.mp4_quality} />}
              </div>
            </div>
            <div className="cn-video__meta">{fmtMonDay(r.created_at)} · {fmtDuration(r.duration_ms)} · {r.views} views</div>
          </div>
        ))}
      </section>
    </div>
  );
}
